import React, { useState, useEffect, useRef } from 'react';
import { colors, commonStyles } from '../theme';
import { translator } from '../i18n';

const IMAGE_MAX_HEIGHT = { small: '200px', medium: '350px', large: '500px' };

export function getQuestionOptions(question) {
  if (question.options && question.options.length > 0) {
    return question.options.map((o) =>
      typeof o === 'string' ? { label: '', text: o } : o
    );
  }
  return ['a', 'b', 'c', 'd']
    .map((letter, i) => {
      const text = question[`option_${letter}`];
      if (!text || !String(text).trim()) return null;
      return { label: String.fromCharCode(65 + i), text: String(text).trim() };
    })
    .filter(Boolean);
}

function QuestionDisplay({
  questions,
  onSubmit,
  teamName,
  connected = true,
  round = null,
  language = 'en',
  savedAnswers = null,
  onDraftChange = () => {},
}) {
  const [answers, setAnswers] = useState({});
  const [textAnswers, setTextAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const tt = translator(language);

  // Questions this phone has changed since the round appeared. The saved
  // answers from the server arrive a moment after the questions do; if a team
  // already tapped something in that gap, their tap wins over the older copy.
  const touchedRef = useRef(new Set());

  // Restore what the server holds for this team: after a refresh, after the
  // phone was asleep, or when a closed round is reopened. Without this a
  // refresh mid-round used to wipe every answer that hadn't been submitted.
  useEffect(() => {
    if (!savedAnswers) return;
    const restoredChoices = {};
    const restoredTexts = {};
    Object.entries(savedAnswers).forEach(([questionId, saved]) => {
      if (touchedRef.current.has(String(questionId))) return;
      if (saved.answerText != null) restoredTexts[questionId] = saved.answerText;
      else if (Array.isArray(saved.selectedAnswers)) restoredChoices[questionId] = saved.selectedAnswers;
      else if (saved.selectedAnswer) restoredChoices[questionId] = saved.selectedAnswer;
    });
    setAnswers((prev) => ({ ...restoredChoices, ...prev }));
    setTextAnswers((prev) => ({ ...restoredTexts, ...prev }));
  }, [savedAnswers]);

  // Every change goes to the server straight away as a draft, so a round the
  // quizmaster closes before this team presses Submit still keeps its answers.
  const report = (questionId, value) => {
    touchedRef.current.add(String(questionId));
    onDraftChange(questionId, value);
  };

  const handleAnswerChange = (questionId, answer) => {
    setAnswers({ ...answers, [questionId]: answer });
    report(questionId, { selectedAnswer: answer });
  };

  const handleMultiAnswerChange = (questionId, answer) => {
    const current = Array.isArray(answers[questionId]) ? answers[questionId] : [];
    const next = current.includes(answer)
      ? current.filter((item) => item !== answer)
      : [...current, answer];
    setAnswers({ ...answers, [questionId]: next });
    report(questionId, { selectedAnswers: next });
  };

  const handleTextChange = (questionId, text) => {
    setTextAnswers({ ...textAnswers, [questionId]: text });
    report(questionId, { answerText: text });
  };

  const handleSubmit = () => {
    if (!connected || submitting) return;

    const allAnswered = questions.every(q => {
      if (q.question_type === 'open') {
        return textAnswers[q.id] && textAnswers[q.id].trim();
      }
      if (q.answer_mode === 'multi') {
        return Array.isArray(answers[q.id]) && answers[q.id].length > 0;
      }
      return answers[q.id];
    });

    if (!allAnswered) {
      alert(tt('answerAllFirst'));
      return;
    }

    setSubmitting(true);
    const answersArray = questions.map(q => {
      if (q.question_type === 'open') {
        return { questionId: q.id, answerText: textAnswers[q.id] };
      }
      if (q.answer_mode === 'multi') {
        return { questionId: q.id, selectedAnswers: answers[q.id] || [] };
      }
      return { questionId: q.id, selectedAnswer: answers[q.id] };
    });
    // If the submit doesn't succeed (dropped connection, timeout), re-enable
    // the button instead of leaving it stuck on "Submitting..." forever.
    onSubmit(answersArray, (result) => {
      if (!result || !result.success) setSubmitting(false);
    });
  };

  // Count answered questions for progress indicator
  const answeredCount = questions.filter(q => {
    if (q.question_type === 'open') return textAnswers[q.id] && textAnswers[q.id].trim();
    if (q.answer_mode === 'multi') {
      return Array.isArray(answers[q.id]) && answers[q.id].length > 0;
    }
    return answers[q.id];
  }).length;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerInner}>
        <div style={styles.headerTop}>
          <span style={styles.teamBadge}>⚡ {teamName}</span>
          <span style={styles.progress}>
            {tt('answeredCount', { answered: answeredCount, total: questions.length })}
          </span>
        </div>
        {/* Which round teams are in — "Round 2 — Music" / "Ronde 2 — Music".
            The round's name is yours; only the word "Round" is translated. */}
        {round && round.number != null && (
          <h1 style={styles.roundTitle}>
            {tt('round', { number: round.number })}
            {round.name ? <span style={styles.roundName}> — {round.name}</span> : null}
          </h1>
        )}
        {/* Progress bar */}
        <div style={styles.progressBar}>
          <div style={{
            ...styles.progressFill,
            width: `${(answeredCount / questions.length) * 100}%`,
          }} />
        </div>
        </div>
      </div>

      <div style={styles.questionsContainer}>
        {questions.map((question, index) => (
          <div key={question.id} style={styles.questionCard}>
            {/* No question number here by design — teams see the question and
                nothing else above it. The internal `title` is never sent. */}
            {question.question_text && String(question.question_text).trim() ? (
              <p style={styles.questionText}>{question.question_text}</p>
            ) : null}

            {question.image_url && (
              <div style={styles.imageContainer}>
                <img
                  src={question.image_url}
                  alt={`Question ${index + 1}`}
                  style={{
                    ...styles.questionImage,
                    maxHeight: IMAGE_MAX_HEIGHT[question.image_size] || IMAGE_MAX_HEIGHT.medium,
                  }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
            )}

            {question.question_type === 'open' ? (
              <textarea
                placeholder={tt('openAnswerPlaceholder')}
                value={textAnswers[question.id] || ''}
                onChange={(e) => handleTextChange(question.id, e.target.value)}
                style={styles.openInput}
                rows="3"
                disabled={submitting}
              />
            ) : (
              <div style={styles.optionsContainer}>
                {getQuestionOptions(question).map((opt) => {
                  const isMulti = question.answer_mode === 'multi';
                  // Cosmetic only. The letter is still what gets submitted,
                  // scored and exported — hiding it just changes what teams
                  // read, so "A. Rens, B. Freddy" becomes "Rens, Freddy".
                  const showLetters = question.show_option_letters !== 0;
                  const selectedValues = Array.isArray(answers[question.id]) ? answers[question.id] : [];
                  const isSelected = isMulti
                    ? selectedValues.includes(opt.label)
                    : answers[question.id] === opt.label;
                  return (
                    <label
                      key={opt.label}
                      style={{
                        ...styles.optionLabel,
                        ...(isSelected ? styles.optionSelected : {}),
                        ...(showLetters ? {} : styles.optionLabelNoLetter),
                      }}
                    >
                      {showLetters && (
                        <span style={{
                          ...styles.optionLetter,
                          ...(isSelected ? styles.optionLetterSelected : {}),
                        }}>
                          {opt.label}
                        </span>
                      )}
                      <input
                        type={isMulti ? 'checkbox' : 'radio'}
                        name={`question-${question.id}`}
                        value={opt.label}
                        checked={isSelected}
                        onChange={() => isMulti
                          ? handleMultiAnswerChange(question.id, opt.label)
                          : handleAnswerChange(question.id, opt.label)}
                        style={{ display: 'none' }}
                        disabled={submitting}
                      />
                      <span style={styles.optionText}>{opt.text}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>

      <div style={styles.submitContainer}>
        <button
          onClick={handleSubmit}
          style={{
            ...styles.submitButton,
            ...(answeredCount < questions.length || !connected ? styles.submitDisabled : {}),
          }}
          disabled={submitting || !connected}
        >
          {!connected ? tt('reconnectingShort') : submitting ? tt('submitting') : tt('submitAnswers')}
        </button>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: colors.bg,
    color: colors.text,
    padding: '20px',
    fontFamily: "'Outfit', 'Segoe UI', sans-serif",
  },
  // Sticky so the round name and the answered count stay in view while
  // scrolling a long round. It spans the container's 20px padding (negative
  // margins) so nothing scrolls past visibly at its edges, and has a solid
  // background so cards slide *under* it rather than showing through.
  header: {
    position: 'sticky',
    top: 0,
    zIndex: 10,
    margin: '-20px -20px 24px',
    padding: '16px 20px 14px',
    backgroundColor: colors.bg,
    borderBottom: `1px solid ${colors.border}`,
    boxShadow: '0 8px 16px -12px rgba(0, 0, 0, 0.8)',
  },
  headerInner: {
    maxWidth: '800px',
    margin: '0 auto',
  },
  headerTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  teamBadge: {
    ...commonStyles.badgeOrange,
    fontSize: '14px',
    padding: '6px 14px',
  },
  progress: {
    color: colors.textMuted,
    fontSize: '14px',
    fontWeight: '600',
  },
  roundTitle: {
    fontSize: '22px',
    fontWeight: '800',
    margin: '0 0 12px',
    letterSpacing: '-0.3px',
  },
  roundName: {
    color: colors.primary,
    // The dash sits tight against the round number at this weight without it.
    marginLeft: '4px',
  },
  progressBar: {
    width: '100%',
    height: '4px',
    backgroundColor: colors.border,
    borderRadius: '2px',
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    backgroundColor: colors.primary,
    borderRadius: '2px',
    transition: 'width 0.3s ease',
  },
  questionsContainer: {
    maxWidth: '800px',
    margin: '0 auto',
  },
  questionCard: {
    backgroundColor: colors.bgCard,
    padding: '28px',
    borderRadius: '16px',
    marginBottom: '16px',
    border: `1px solid ${colors.border}`,
    animation: 'slideUp 0.4s ease',
  },
  imageContainer: {
    marginBottom: '16px',
    textAlign: 'center',
  },
  questionImage: {
    maxWidth: '100%',
    maxHeight: '350px',
    borderRadius: '12px',
    objectFit: 'contain',
  },
  questionText: {
    fontSize: '18px',
    marginBottom: '20px',
    lineHeight: '1.6',
    fontWeight: '500',
  },
  optionsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  optionLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    padding: '14px 16px',
    backgroundColor: colors.bgInput,
    borderRadius: '12px',
    cursor: 'pointer',
    border: `1px solid ${colors.border}`,
    transition: 'all 0.2s ease',
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',
    userSelect: 'none',
  },
  optionSelected: {
    backgroundColor: colors.primaryMuted,
    // Must be the `border` shorthand, the same key the base style uses.
    // A `borderColor` overlay gets *removed* by React on deselect, and a
    // removed longhand falls back to currentColor (white): that was the white
    // outline left on every option a team had tapped before changing its mind.
    border: `1px solid ${colors.primary}`,
  },
  // Without the letter badge the row loses its left inset, so add it back —
  // otherwise a lettered and a letterless question look misaligned.
  optionLabelNoLetter: {
    paddingLeft: '20px',
  },
  optionLetter: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: '700',
    backgroundColor: colors.border,
    color: colors.textMuted,
    flexShrink: 0,
  },
  optionLetterSelected: {
    backgroundColor: colors.primary,
    color: '#fff',
  },
  optionText: {
    fontSize: '16px',
  },
  openInput: {
    width: '100%',
    padding: '14px 16px',
    fontSize: '16px',
    backgroundColor: colors.bgInput,
    color: colors.text,
    border: `1px solid ${colors.border}`,
    borderRadius: '12px',
    boxSizing: 'border-box',
    resize: 'vertical',
    fontFamily: 'inherit',
    outline: 'none',
  },
  submitContainer: {
    maxWidth: '800px',
    margin: '24px auto 40px',
    textAlign: 'center',
  },
  submitButton: {
    padding: '16px 60px',
    fontSize: '18px',
    backgroundColor: colors.primary,
    color: '#fff',
    border: 'none',
    borderRadius: '12px',
    cursor: 'pointer',
    fontWeight: '700',
    letterSpacing: '0.5px',
    transition: 'all 0.2s ease',
  },
  submitDisabled: {
    opacity: 0.6,
  },
};

export default QuestionDisplay;
